import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info, Plus, Shield } from "lucide-react";
import { toast } from "../../../../utils/toast";
import { Card } from "../../../../components/ui/Card";
import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { usePermission } from "../../../../hooks/usePermission";
import {
  getRoles,
  getRolesCatalog,
  deleteRole,
  deleteBaseRoleOverride,
  rolesErrorMessage,
} from "../../../../api/roles";
import { cn } from "../../../../utils/cn";
import RoleEditor from "./RoleEditor";
import { ConfirmModal, RoleFormModal } from "./RoleDialogs";
import { ROLE_KINDS, groupCatalog, indexCatalog, roleKindLabel } from "./rolesModel";

const EMPTY = { roles: [], resources: [] };

/**
 * Pestaña "Roles y permisos" (R3.2): lista de roles del operador y editor de
 * la matriz de permisos. Toda la autorización real vive en microservice-users;
 * acá los permisos del token solo deciden qué acciones se ofrecen.
 */
export default function RolesPermisos() {
  const { t } = useTranslation();
  // `t` puede cambiar de identidad en cada render (y al cambiar de idioma): si `load`
  // dependiera de él, el efecto de carga volvería a pedir los roles en bucle.
  const tRef = useRef(t);
  tRef.current = t;
  const canCreate = usePermission("role:create");
  const canUpdate = usePermission("role:update");
  const canDelete = usePermission("role:delete");

  const [{ roles, resources }, setData] = useState(EMPTY);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [loadError, setLoadError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [pendingSelect, setPendingSelect] = useState(null);
  // { type: "create"|"duplicate"|"rename"|"delete"|"reset", role? }
  const [dialog, setDialog] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const load = useCallback(async () => {
    const [rolesRes, catalogRes] = await Promise.all([getRoles(), getRolesCatalog()]);
    const failure = rolesRes.error || catalogRes.error;
    if (failure) {
      const tr = tRef.current;
      setLoadError(rolesErrorMessage(failure, tr, tr("No se pudieron cargar los roles. Intenta de nuevo.")));
      setStatus("error");
      return null;
    }
    const nextRoles = Array.isArray(rolesRes.data?.roles) ? rolesRes.data.roles : [];
    setData({
      roles: nextRoles,
      resources: Array.isArray(catalogRes.data?.resources) ? catalogRes.data.resources : [],
    });
    setStatus("ready");
    return nextRoles;
  }, []);

  useEffect(() => {
    let alive = true;
    load().then((list) => {
      if (alive && list) setSelectedId((prev) => (list.some((r) => r.id === prev) ? prev : list[0]?.id ?? null));
    });
    return () => { alive = false; };
  }, [load]);

  const groups = useMemo(() => groupCatalog(resources, t), [resources, t]);
  const catalogIndex = useMemo(() => indexCatalog(resources), [resources]);
  const grantable = useMemo(
    () => new Set(Array.from(catalogIndex).filter(([, v]) => v.grantable).map(([k]) => k)),
    [catalogIndex],
  );
  const role = roles.find((r) => r.id === selectedId) || null;

  const reloadAndSelect = async (pick) => {
    const list = await load();
    if (!list) return;
    const picked = pick ? pick(list) : null;
    setSelectedId(picked?.id ?? (list.some((r) => r.id === selectedId) ? selectedId : list[0]?.id ?? null));
  };

  const requestSelect = (id) => {
    if (id === selectedId) return;
    if (dirty) setPendingSelect(id);
    else setSelectedId(id);
  };

  const closeDialog = () => { setDialog(null); setActionError(""); setActionBusy(false); };

  const runAction = async (fn, successMessage, pick) => {
    setActionBusy(true);
    setActionError("");
    const { error } = await fn();
    setActionBusy(false);
    if (error) {
      setActionError(rolesErrorMessage(error, t, t("No se pudo completar la operación. Intenta de nuevo.")));
      return;
    }
    toast.success(successMessage);
    closeDialog();
    await reloadAndSelect(pick);
  };

  const confirmDelete = () => runAction(
    () => deleteRole(dialog.role.id),
    t("Rol eliminado"),
    (list) => list.find((r) => r.kind !== ROLE_KINDS.CUSTOM && r.baseRoleId === dialog.role.baseRoleId),
  );
  const confirmReset = () => runAction(
    () => deleteBaseRoleOverride(dialog.role.baseRoleId),
    t("Volvió a la plantilla"),
    (list) => list.find((r) => r.kind !== ROLE_KINDS.CUSTOM && r.baseRoleId === dialog.role.baseRoleId),
  );

  const onSaved = (saved) => reloadAndSelect(
    saved.kind === ROLE_KINDS.CUSTOM
      ? (list) => list.find((r) => r.id === saved.id)
      : (list) => list.find((r) => r.kind !== ROLE_KINDS.CUSTOM && r.baseRoleId === saved.baseRoleId),
  );

  const onFormDone = (created) => {
    const mode = dialog.type;
    const sourceId = dialog.role?.id;
    closeDialog();
    toast.success(mode === "rename" ? t("Rol renombrado") : t("Rol creado"));
    const newId = created?.id ?? created?._id ?? (mode === "rename" ? sourceId : null);
    reloadAndSelect(newId ? (list) => list.find((r) => r.id === newId) : null);
  };

  const noticeText = t("Los cambios se aplican a cada usuario la próxima vez que se renueve su sesión (máximo 1 hora).");

  if (status === "loading") {
    return (
      <div className="space-y-4" aria-busy="true" aria-label={t("Cargando roles")}>
        <div className="h-10 rounded-lg bg-bg-subtle animate-pulse" />
        <div className="h-72 rounded-lg bg-bg-subtle animate-pulse" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <EmptyState
        icon={<Shield />}
        title={t("No se pudieron cargar los roles")}
        description={loadError}
        action={<Button variant="outline" onClick={() => { setStatus("loading"); load().then((l) => l && setSelectedId(l[0]?.id ?? null)); }}>{t("Reintentar")}</Button>}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p role="note" className="flex items-start gap-2 rounded-lg border border-border bg-bg px-3 py-2 text-xs text-fg-muted">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>{noticeText}</span>
      </p>

      <Card padding="none" className="overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)]">
          <div className="border-b border-border lg:border-b-0 lg:border-r">
            <div className="flex items-center justify-between gap-2 px-4 py-3">
              <h3 className="text-sm font-semibold text-fg">{t("Roles")}</h3>
              {canCreate && (
                <Button size="sm" iconLeft={<Plus className="h-4 w-4" />} onClick={() => setDialog({ type: "create" })}>
                  {t("Crear rol")}
                </Button>
              )}
            </div>
            {roles.length === 0 ? (
              <p className="px-4 pb-4 text-sm text-fg-muted">{t("Todavía no hay roles para mostrar.")}</p>
            ) : (
              <ul aria-label={t("Roles")} className="divide-y divide-border border-t border-border">
                {roles.map((r) => {
                  const active = r.id === selectedId;
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        aria-current={active ? "true" : undefined}
                        onClick={() => requestSelect(r.id)}
                        className={cn(
                          "w-full px-4 py-3 text-left transition-colors hover:bg-bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500",
                          active && "bg-brand-50",
                        )}
                      >
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className={cn("text-sm font-semibold break-words", active ? "text-brand-700" : "text-fg")}>{r.name}</span>
                          {r.kind === ROLE_KINDS.OVERRIDE && <Badge tone="warning">{t("Personalizado")}</Badge>}
                        </span>
                        <span className="mt-0.5 block text-xs text-fg-muted">
                          {roleKindLabel(r.kind, t)}
                          {r.kind === ROLE_KINDS.CUSTOM ? ` · ${t("Base: {{base}}", { base: r.baseRoleName })}` : ""}
                          {` · ${t("Usuarios: {{n}}", { n: r.userCount ?? 0 })}`}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {role ? (
            <RoleEditor
              key={`${role.id}:${role.permissions.join("|")}`}
              role={role}
              groups={groups}
              catalogIndex={catalogIndex}
              canUpdate={canUpdate}
              canCreate={canCreate}
              canDelete={canDelete}
              onSaved={onSaved}
              onDirtyChange={setDirty}
              onRename={(r) => setDialog({ type: "rename", role: r })}
              onDuplicate={(r) => setDialog({ type: "duplicate", role: r })}
              onDelete={(r) => setDialog({ type: "delete", role: r })}
              onReset={(r) => setDialog({ type: "reset", role: r })}
            />
          ) : (
            <EmptyState icon={<Shield />} title={t("Selecciona un rol")} description={t("Elige un rol de la lista para ver sus permisos.")} />
          )}
        </div>
      </Card>

      <RoleFormModal
        mode={dialog?.type === "create" || dialog?.type === "duplicate" || dialog?.type === "rename" ? dialog.type : "create"}
        open={dialog?.type === "create" || dialog?.type === "duplicate" || dialog?.type === "rename"}
        roles={roles}
        source={dialog?.role}
        grantable={grantable}
        onClose={closeDialog}
        onDone={onFormDone}
      />

      <ConfirmModal
        open={dialog?.type === "delete"}
        title={t("Eliminar rol")}
        confirmLabel={t("Eliminar")}
        confirmVariant="danger"
        confirmDisabled={(dialog?.role?.userCount ?? 0) > 0}
        busy={actionBusy}
        error={actionError}
        onConfirm={confirmDelete}
        onCancel={closeDialog}
      >
        {(dialog?.role?.userCount ?? 0) > 0 ? (
          <p role="note" className="rounded-lg border border-status-warning bg-status-warning-soft px-3 py-2 text-status-warning">
            {t("No se puede eliminar el rol porque todavía tiene usuarios asignados (usuarios: {{n}}). Reasígnalos antes de eliminarlo.", { n: dialog.role.userCount })}
          </p>
        ) : (
          <p>{t("Vas a eliminar el rol “{{name}}”. Esta acción no se puede deshacer.", { name: dialog?.role?.name || "" })}</p>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={dialog?.type === "reset"}
        title={t("Volver a la plantilla")}
        confirmLabel={t("Volver a la plantilla")}
        busy={actionBusy}
        error={actionError}
        onConfirm={confirmReset}
        onCancel={closeDialog}
      >
        <p>{t("Se descarta la personalización de “{{name}}” y vuelve a los permisos de la plantilla global.", { name: dialog?.role?.name || "" })}</p>
        <p className="text-fg-muted">{noticeText}</p>
      </ConfirmModal>

      <ConfirmModal
        open={pendingSelect !== null}
        title={t("Cambios sin guardar")}
        confirmLabel={t("Descartar cambios")}
        confirmVariant="danger"
        onConfirm={() => { setSelectedId(pendingSelect); setPendingSelect(null); }}
        onCancel={() => setPendingSelect(null)}
      >
        <p>{t("Tienes cambios sin guardar en este rol. Si cambias de rol se pierden.")}</p>
      </ConfirmModal>
    </div>
  );
}
